import React, { FC, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Nav from 'react-bootstrap/Nav';
import { FaChevronDown } from 'react-icons/fa';
import styled from 'styled-components';

import { useActiveClientContext } from 'store/active-client';
import { isActiveRoute } from 'utils/helpers/routing';
import { useTranslation } from 'react-i18next';
import SubMenu from './SubMenu';
import { IMenuItem } from './types';

interface IProps extends IMenuItem {
  childrenRoutes?: IMenuItem[];
}

const Dropdown: FC<IProps> = ({ childrenRoutes, label, to, icon: Icon, ariaLabel }: IProps) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const { client } = useActiveClientContext();
  const location = useLocation();

  const { t } = useTranslation();

  return childrenRoutes ? (
    <NavLinkDropdown
      aria-label={ariaLabel}
      ref={setReferenceElement}
      className="font-weight-normal nav-link mt-2 mt-md-0 mb-2 mb-md-0"
      onMouseEnter={() => setIsMenuVisible(true)}
      onMouseLeave={() => setIsMenuVisible(false)}
      isActive={childrenRoutes.map(route => route.to).includes(location.pathname)}
    >
      <Icon
        size={20}
        style={{ marginRight: '0.5rem' }}
      />
      {label}
      <FaChevronDown
        size={12}
        style={{ marginLeft: '0.5rem' }}
      />
      <SubMenu
        refElement={referenceElement}
        visible={isMenuVisible}
        onMouseLeave={() => setIsMenuVisible(false)}
      >
        <SubNav>
          {childrenRoutes.map(item => (
            <StyledNavLink
              key={item.label}
              isActive={isActiveRoute(location, item.to, client)}
            >
              <Link
                className="nav-link"
                to={item.to}
              >
                <item.icon
                  size={15}
                  style={{ marginRight: '0.5rem' }}
                />
                {t(item.label)}
              </Link>
            </StyledNavLink>
          ))}
        </SubNav>
      </SubMenu>
    </NavLinkDropdown>
  ) : (
    <></>
  );
};

export default Dropdown;

const StyledNavLink = styled.span<{ isActive: boolean }>`
  a {
    padding: 0.5rem 1rem !important;
    display: flex;
    align-items: center;
    color: ${props => (props.isActive ? props.theme.colors.violet : props.theme.colors.navbarLink)} !important;

    &:hover {
      color: ${props => props.theme.colors.violet} !important;
    }

    @media ${props => props.theme.breakpoints.md} {
      padding: 0 !important;
    }
  }
`;

const NavLinkDropdown = styled.a<{ isActive: boolean }>`
  padding: 0 1rem !important;
  display: flex;
  position: relative;
  align-items: center;
  cursor: pointer;
  color: ${props => (props.isActive ? props.theme.colors.violet : props.theme.colors.navbarLink)} !important;

  > a {
    color: ${props => props.theme.colors.navbarLink} !important;
  }

  &:hover {
    color: ${props => props.theme.colors.violet} !important;
    > a {
      color: ${props => props.theme.colors.navbarLink} !important;
    }
  }

  @media ${props => props.theme.breakpoints.md} {
    padding: 0 !important;
  }
`;

const SubNav = styled(Nav)`
  display: flex;
  flex-direction: column !important;
  padding: 1rem 0;
`;
